import React, { useState, useEffect, useRef } from "react";
import PropTypes from 'prop-types';
import Modal from '../Modal/Modal';
import './Login.css';
import purify from "dompurify";
import { useNavigate } from 'react-router-dom';

const Login = React.memo( 
  ({
    isFetching,
    errorMessage,
    fetchAccessToken,
    verifyOtp,
    sessionId,
    resendOtp,
    accessToken,
    otpAttemptCount
  }) => {
    const [loginId, setLoginId] = useState("");
    const [password, setPassword] = useState("");
    const [remember, setRemember] = useState(false);
    const [showOtp, setShowOtp] = useState(false);
    const [otpCode, setOtpCode] = useState("");
    const [resendNotification, setResendNotification] = useState(false);
    const [countdown, setCountdown] = useState(30);
    const [localErrorMessage, setLocalErrorMessage] = useState('');
    const loginIdRef = useRef(null);
    const passwordRef = useRef(null);
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [isResending, setIsResending] = useState(false);

    /* -----------------------------------------------
    * OTPトークンが取得されたらOTP入力画面へ切替
    * ----------------------------------------------- */
    useEffect(() => {
      if (sessionId) {
        setShowOtp(true);
      }
    }, [sessionId]);

    // ログアウト後のリダイレクト制御用フラグを初期化する。
    useEffect(() => {
      const lastAction = localStorage.getItem("lastAction");
      if (lastAction === "logout") {
        localStorage.removeItem("lastAction");
      }
    }, []);
    
    /* -----------------------------------------------
     * 初期表示時の処理
     * - 「ログインID記憶」設定の読み込み
     * - 既にアクセストークンがある場合はユーザー情報取得
     * ----------------------------------------------- */
    useEffect(() => {
      const storedLoginId = localStorage.getItem('login_id');
      const storedRemember = localStorage.getItem('remember') === 'true';
      setRemember(storedRemember);
      if (storedRemember) setLoginId(storedLoginId || '');

      const accessTokenLocal = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');

      if (accessToken || accessTokenLocal) {
        const tokenToUse = accessToken || accessTokenLocal;

        if (tokenToUse && refreshToken) {
          // fetchUserData(tokenToUse);
          const redirectPath =
            localStorage.getItem("redirectAfterLogin") || "/admin-list";

          localStorage.removeItem("redirectAfterLogin");
          navigate(redirectPath);
        }
      }
    }, [accessToken, navigate]);

    // OTPを3回誤入力した場合 → ログイン画面へ戻る
    useEffect(() => {
      if (otpAttemptCount >= 3) {

        // ローカルUIをリセット
        setOtpCode("");
        setShowOtp(false);
        setCountdown(30);
        setResendNotification(false);

        // OTPフローをリセット（Reduxで保持している情報を初期化）
        localStorage.removeItem("password");
        localStorage.removeItem("session_id");

        // ログイン画面へ遷移
        navigate("/login");

      }
    }, [otpAttemptCount]);

    // ログインボタンクリック時
    const handleLogin = async (e) => {
      e.preventDefault();

      if (!loginId) {
        setLocalErrorMessage("ユーザーIDが入力されていません。");
        loginIdRef.current?.focus();
        return;
      }

      if (!password) {
        setLocalErrorMessage("パスワードが入力されていません。");
        if (remember) {
          // CASE 1: checked remember → focus password
          passwordRef.current?.focus();
        } else {
          setLoginId("");
          // CASE 2: not checked remember → focus loginId
          loginIdRef.current?.focus();
        }
        return;
      }

      setLocalErrorMessage("");

      try {
        await fetchAccessToken({
          login_id: loginId,
          password: password,
        });
      } catch (error) {
        if (remember) {
          loginIdRef.current?.select();
        } else {
          setLoginId('');
          loginIdRef.current?.focus();
        }
      }
    };

    // アクセストークン取得後に「ログインIDを記憶する」設定を反映する
    useEffect(() => {
      if (accessToken) {
        if (remember) {
          localStorage.setItem("remember", "true");
          localStorage.setItem("login_id", loginId);
        } else {
          localStorage.removeItem("remember");
          localStorage.removeItem("login_id");
        }
      }
    }, [accessToken]); 

    //OTP認証ボタンクリック時
    const handleVerifyOtp = async (e) => {
      e.preventDefault();

      if (!otpCode.trim()) {
        setLocalErrorMessage("認証コードを入力してください。");
        return;
      }

      setLocalErrorMessage("");
      setResendNotification(false);
      
      try {
        await verifyOtp(otpCode);

      } catch (error) {
        setOtpCode('');
        setResendNotification(true);
      }
    };

    //OTP入力値変更時
    const handleOtpChange = (e) => {
      setOtpCode(e.target.value);
    };

    // 「再送」リンククリック時
    const handleResendClick = async () => {
      if (isResending) return;

      setIsResending(true);

      try {
        await resendOtp();

        setCountdown(30);
        setOtpCode('');
        setResendNotification(false);
        setModalMessage("認証コードを再送信しました。");
        setIsModalOpen(true);
      } catch (error) {
        console.error("resend OTP error:", error);
        setLocalErrorMessage(error.message || "認証コードの再送信に失敗しました。");
      } finally {
        setTimeout(() => setIsResending(false), 30000);
      }
    };

    //「ログインIDを記憶する」チェック変更時
    const handleCheckboxChange = (e) => {
      setRemember(e.target.checked);
    }; 

    useEffect(() => {
      const interval = setInterval(() => {
        if (otpCode  || showOtp) {
          if (countdown > 0) {
            setCountdown(countdown - 1);
          } else {
            clearInterval(interval);
            setResendNotification(true);
          }
        }
      }, 1000);

      return () => clearInterval(interval);
    }, [countdown, showOtp]);

    return (
      <div className="lay-login">
        {isModalOpen && (
          <Modal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            message={modalMessage}
          />
        )}
        <div className="lay-login__sub">
          <div className="center">
            <img src="/img/common/navi/logo.png" alt="Logo" className="center" />
          </div>
          <div className="hpc15">
            <span className="login-title">発番システム（管理）</span>
            <span className="login-subtitle">HPC-15</span>
          </div>

          {!showOtp ? (
            <div className="login-form">
              <form onSubmit={handleLogin}>
                <label className="login-label">ユーザーID:</label>
                <input
                  type="text"
                  className="form-control"
                  value={loginId}
                  ref={loginIdRef}
                  onChange={(e) => setLoginId(e.target.value.toLowerCase())}
                />

                <label className="login-label">パスワード:</label>
                <input
                  type="password"
                  className="form-control"
                  value={password}
                  ref={passwordRef}
                  onChange={(e) => setPassword(e.target.value)}
                />

                <div className="checkbox-frame">
                  <label>
                    <input
                      type="checkbox"
                      id="remember"
                      checked={remember}
                      name="checkbox"
                      className="checkbox-input"
                      onChange={handleCheckboxChange}
                    />
                    <span className="checkbox-parts">
                       ユーザーIDを記憶する
                    </span>
                  </label>
                </div>

                {(localErrorMessage || errorMessage) && (
                  <p className="error" dangerouslySetInnerHTML={{ __html: purify.sanitize(localErrorMessage || errorMessage) }} />
                )}

                <button
                  type="submit"
                  className="login-button"
                  disabled={isFetching}
                >
                  ログイン
                  {isFetching && <div className="login-spinner"></div>}
                </button>
              </form>
            </div>
          ) : (
            <div className="login-form">
              {(localErrorMessage || errorMessage) && (
                <p className="error" dangerouslySetInnerHTML={{ __html: purify.sanitize(localErrorMessage || errorMessage) }} />
              )}
              <form id="otp-form" method="post" onSubmit={handleVerifyOtp}>
                <div className="otp-check-container">
                  <div className="otp-check-div">
                    <label className="login-label">認証コード</label>
                  </div>
                  <div className="otp-check-div">
                    <input
                      type="text"
                      id="otp_code"
                      name="otp_code"
                      maxLength="6"
                      autoComplete="off"
                      value={otpCode}
                      onChange={handleOtpChange}
                    />
                  </div>
                  <div className="otp-check-div" style={{ marginTop: "40px", marginBottom: '10px' }}>
                    <button type="submit" className="btn login-button">認証{isFetching && <div className="login-spinner"></div>}</button>
                  </div>
                </div>
              </form>
              {resendNotification && (
                <div id="resend-otp">
                  <span>
                    認証コードが受信できない場合、
                    <em
                      onClick={handleResendClick}
                      style={{
                        cursor: isResending ? 'not-allowed' : 'pointer',
                        textDecoration: isResending ? 'none' : 'underline',
                        opacity: isResending ? 0.5 : 1,
                      }}
                    >
                      {isResending ? '再送中...' : 'こちら'}
                    </em>
                    で再試行してください。
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
);

Login.displayName = "Login";    // fix eslint(react/display-name)

Login.propTypes = {
  isFetching: PropTypes.bool.isRequired,
  sessionId: PropTypes.string,
  accessToken: PropTypes.string,
  refreshToken: PropTypes.string,
  errorCode: PropTypes.string,
  errorMessage: PropTypes.string,
  fetchAccessToken: PropTypes.func.isRequired,
  verifyOtp: PropTypes.func.isRequired,
  resendOtp: PropTypes.func.isRequired
};

export default Login;
